import { useRef, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, ButtonSize, Rounding } from '../../../shared/components/button/button';
import { Icon } from '../../../shared/components/icon/icon';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import type { FCWithChildren } from '../../../shared/utils/component.interface';
import { capitalize } from '../../../shared/utils/strings';

import styles from './drag-and-drop.module.scss';

const style = createStyleHelper(styles, 'drag-and-drop');

export interface IDragAndDropProps {
    className?: string;
    onChange(fileList?: FileList | undefined): void;
    value?: FileList | null;
    side: string;
    error?: string | JSX.Element;
}

export const DragAndDrop: FCWithChildren<IDragAndDropProps> = ({
    className,
    onChange,
    value = null,
    side,
    error,
    children,
}) => {
    const isDesktop = useDesktop();
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!fileRef.current) {
            return;
        }

        fileRef.current.files = value;
    }, [value]);

    const handleSelectedFile = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.files ?? undefined);
        },
        [onChange],
    );

    const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }, []);

    const processFile = useCallback(
        (files: FileList) => {
            if (!fileRef.current) {
                return;
            }

            fileRef.current.files = files;
            onChange(files);
        },
        [onChange],
    );

    const onDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            const {
                dataTransfer: { files },
            } = event;

            const { length } = files;

            if (length === 0) {
                return false;
            }

            processFile(files);
        },
        [processFile],
    );

    return (
        <div className={createClassName([className, style()])}>
            <div
                className={style('drop-area')}
                onDrop={(event) => onDrop(event)}
                onDragOver={(event) => onDragOver(event)}
            >
                {!value ? (
                    <div className={style('without-document')}>
                        <div className={style('file-icon')}>
                            <Icon name={'file-alt'} className={style('icon-file')} />
                        </div>
                        <div className={style('upload-document-section')}>
                            <div className={style('upload-text-section')}>
                                {error ? (
                                    <div className={style('error-title')}>
                                        <FormattedMessage
                                            id={'account.verification.drag-and-drop.upload-failure'}
                                            defaultMessage={'Upload Failure'}
                                        />
                                    </div>
                                ) : (
                                    <FormattedMessage
                                        id={'account.verification.drag-and-drop.upload-document'}
                                        defaultMessage={'Upload Document {side}'}
                                        values={{ side: capitalize(side.toLocaleLowerCase()) }}
                                    />
                                )}
                            </div>
                            {isDesktop && (
                                <div className={style('drop-file-text-section')}>
                                    {error ? (
                                        <>{error}</>
                                    ) : (
                                        <FormattedMessage
                                            id={
                                                'account.verification.drag-and-drop.drop-your-files-here'
                                            }
                                            defaultMessage={
                                                'Drop your files here or select a file from your computer clicking on the button below.'
                                            }
                                        />
                                    )}
                                </div>
                            )}
                            <div className={style('button-section')}>
                                <Button
                                    hollow
                                    className={style('browse-files')}
                                    size={ButtonSize.SMALL}
                                    rounding={Rounding.NORMAL}
                                    onClick={() => fileRef.current?.click()}
                                >
                                    <FormattedMessage
                                        id="account.verification.drag-and-drop.browse-files"
                                        defaultMessage="Browse Files"
                                    />
                                </Button>
                            </div>
                            <input
                                ref={fileRef}
                                onChange={handleSelectedFile}
                                multiple={false}
                                type="file"
                                accept=".jpeg, .jpg, .png, .pdf"
                                hidden
                            />
                        </div>
                    </div>
                ) : (
                    <>{children}</>
                )}
            </div>
        </div>
    );
};
